import { TextInput, Button } from '@pandiafi/design-system'
import { Formik, Field, FieldProps, Form } from 'formik'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { Text } from '@pandiafi/components'

import {
  SetVerificationCodeEmailRequest,
  setVerificationCodeEmailRequestSchema,
} from '../../../common/validations'
import routePath from '../../router/routePath'
import { PageTranslationSafeLabel } from '../../components'

import { useAPIVerifyEmail } from 'api/apiHooks/useAPIVerifyEmail'
import FormFieldWrapper from 'components/FormField'
import { validateFormValues } from 'utils'
import { FormErrorSection } from 'views/Login/FormErrorSection'

export const VerifyEmailForm: React.FC<{
  newPassword: string
  onSuccess: (email: string) => void
  onCancel: () => void
}> = ({ newPassword, onSuccess, onCancel }) => {
  const { t } = useTranslation(['registration'])

  const { mutate, isPending, isError } = useAPIVerifyEmail({
    onSuccess: ({ cognitoId }) => {
      onSuccess(cognitoId)
    },
  })

  const handleSubmit = (values: SetVerificationCodeEmailRequest) => {
    mutate({ ...values, newPassword })
  }

  const handleValidate = (formValues: SetVerificationCodeEmailRequest) => {
    const validationErrors = validateFormValues(
      formValues,
      setVerificationCodeEmailRequestSchema
    )

    return validationErrors
  }

  return (
    <Formik
      initialValues={{
        email: '',
      }}
      onSubmit={handleSubmit}
      validate={handleValidate}
      validateOnMount
    >
      {({ isValid, setFieldValue, setFieldTouched }) => (
        <Form className="flex flex-col space-y-6">
          <Text size="l" weight="medium" element="h3">
            {t('registration:confirmEmailHeader')}
          </Text>
          <Text size="m" element="p">
            {t('registration:confirmEmailDescription')}
          </Text>

          <Field name="email">
            {({
              field,
              meta,
            }: FieldProps<SetVerificationCodeEmailRequest['email']>) => (
              <FormFieldWrapper
                name={field.name}
                meta={meta}
                label={t('registration:confirmEmailLabel')}
              >
                <TextInput
                  {...field}
                  type="email"
                  name="email"
                  onChange={async (e) => {
                    const lowercaseValue = e.target.value.toLowerCase()
                    await setFieldValue('email', lowercaseValue)
                    await setFieldTouched('email', true)
                  }}
                />
              </FormFieldWrapper>
            )}
          </Field>
          {isError && (
            <FormErrorSection
              error={t('registration:confirmEmailSubmitError')}
              additionalLink={{
                url: `${routePath.registerAuth}?resetOldUser=true`,
                text: t('registration:resetRegistrationLinkText'),
              }}
            />
          )}

          <Button
            loading={isPending}
            disabled={!isValid || isPending}
            type="submit"
          >
            <PageTranslationSafeLabel>
              {t('registration:confirmEmailSubmit')}
            </PageTranslationSafeLabel>
          </Button>

          <Button type="button" variant="cancel" onClick={onCancel}>
            <PageTranslationSafeLabel>
              {t('registration:confirmEmaiCancel')}
            </PageTranslationSafeLabel>
          </Button>
        </Form>
      )}
    </Formik>
  )
}
